/**
 * Shared type definitions for import operations
 *
 * This module consolidates all import-related types that are used
 * across multiple import hooks and utilities.
 */

import type { AuthType } from '../User'
import type { Note } from '../note'
import type { AttachmentFailure } from './log'

/**
 * Utility function for exhaustiveness checking in switch statements
 */
export function assertNever(x: never): never {
  throw new Error(`Unexpected object: ${JSON.stringify(x)}`)
}

/**
 * Map of folder paths to their created record names
 * Used to maintain folder hierarchy during imports
 */
export type FolderIdMap = Map<string /*path*/, string /*recordName*/>

/**
 * Resolved location for a note during import
 * Contains the filename and parent folder reference
 */
export type ResolvedLocation = {
  /** The filename (may include path for CloudKit, simple name for Supabase) */
  filename: string
  /** The parent folder UUID (null for root or CloudKit) */
  parent: string | null
}

/**
 * Configuration options for markdown import operations
 */
export type MarkdownImportOptions = {
  /** Map of folder paths to their record names for hierarchy support */
  folderIdMap: FolderIdMap
  /** Map of attachment file paths to File objects */
  attachmentFiles: Map<string, File>
  /** Authentication type for backend-specific processing */
  authType: AuthType
  /** All existing project notes for conflict detection */
  allProjectNotes: Map<string, Note>
  /** Calendar notes organized by date for conflict detection */
  calendarNotes: Map<string, Note[]>
  /** Target folder for import (empty string for root) */
  targetFolder: string
  /** Optional progress callback */
  onProgress?: (done: number, total: number) => void
  /** Optional conflict resolution callback */
  onConflict?: (filename: string, isCalendarFile: boolean) => Promise<ImportConflictChoice>
}

/**
 * Result of an import operation
 */
export type ImportResult = {
  /** Import statistics summary */
  stats: ImportStats
  /** Array of errors that occurred during import */
  errors: ImportError[]
  /** Array of warnings that occurred during import */
  warnings: ImportWarning[]
  /** Array of hints for unused or orphaned files */
  hints: ImportHint[]
  /** Array of attachment upload failures for enhanced error reporting */
  attachmentsFailed: AttachmentFailure[]
}

/**
 * Import statistics used across all import operations
 */
export type ImportStats = {
  /** Number of files successfully created/imported */
  created: number
  /** Number of files skipped due to conflicts or user choice */
  skipped: number
  /** Number of files that failed to import due to errors */
  errors: number
  /** Number of files duplicated (when user chooses duplicate option) */
  duplicated: number
  /** Number of existing files that were overwritten */
  overwritten: number
  /** Number of folders that were newly created */
  foldersCreated: number
  /** Number of folders that already existed and were reused */
  foldersReused: number
}

/**
 * Error details for import operations
 */
export type ImportError = {
  /** File path where the error occurred */
  path: string
  /** Human-readable error message */
  message: string
}

/**
 * Warning details for import operations
 */
export type ImportWarning = {
  /** File path where the warning occurred */
  path: string
  /** Human-readable warning message */
  message: string
}

/**
 * Hint details for import operations
 * Used to provide helpful information about unused or orphaned files
 */
export type ImportHint = {
  /** File path where the hint applies */
  path: string
  /** Human-readable hint message */
  message: string
}

/**
 * User's choice for handling import conflicts
 */
export type ImportConflictChoice = 'overwrite' | 'skip' | 'duplicate' | 'merge'

/**
 * Props for the import conflict dialog component
 */
export type ImportConflictDialogProps = {
  /** Name of the conflicting file */
  filename: string
  /** Whether this is a calendar file */
  isCalendarFile: boolean
}

/**
 * Represents a note to be imported with its metadata
 */
export type ImportNote = {
  /** Full relative path of the note */
  path: string
  /** Source file object */
  file: File
  /** Whether this is auto-detected as a calendar note */
  isCalendar: boolean
  /** Related attachment files */
  attachments: File[]
}

/**
 * Union type for import items - can be either a note or a folder
 * Used by the unified import engine for processing
 */
export type ImportItem =
  | { kind: 'note'; note: ImportNote }
  | { kind: 'folder'; path: string }

/**
 * Type guard to check if an ImportItem is a folder
 */
export function isFolder(
  item: ImportItem
): item is Extract<ImportItem, { kind: 'folder' }> {
  return item.kind === 'folder'
}

/**
 * Type guard to check if an ImportItem is a note
 */
export function isNote(
  item: ImportItem
): item is Extract<ImportItem, { kind: 'note' }> {
  return item.kind === 'note'
}

/**
 * Batch of files organized for import processing
 */
export type ImportBatch = {
  /** Unique folder paths to create */
  folders: Set<string>
  /** Notes with their attachments */
  notes: ImportNote[]
  /** Map of attachment file paths to File objects */
  attachmentFiles: Map<string, File>
}

/**
 * Result of building an import batch with warnings and hints
 * Updated format for unified import engine
 */
export type ImportBatchResult = {
  /** Flattened array of import items (notes and folders) */
  items: ImportItem[]
  /** Warnings generated during batch processing */
  warnings: ImportWarning[]
  /** Hints generated during batch processing */
  hints: ImportHint[]
}

/**
 * Options for processing individual notes with attachments
 */
export type NoteProcessingOptions = {
  /** The note being processed */
  note: ImportNote
  /** Note content */
  content: string
  /** Map of folder paths to record names */
  folderIdMap: FolderIdMap
  /** Map of attachment files */
  attachmentFiles: Map<string, File>
  /** Authentication type */
  authType: AuthType
}
